import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";

import { HeaderTitle } from "../../../../components/HeaderTitle";
import { useNotification } from "../../../../hooks/useNotification";
import { useAuth } from "../../../../hooks/useAuth";

import image from "../../../../assets/jpg/no_image.jpg";

import {
  IAnimal,
  IBatch,
  IFlock,
  IResponseBatchOrFlockAxios,
} from "../../../animals/interface";
import {
  allAnimals,
  getAllBatchs,
  getAllFlocks,
} from "../../../../services/animals";

import { createManagement } from "../../../../services/management";
import { IManagedAnimal, IManagementOptions } from "../../interfaces";

import { useTranslate } from "../../../../hooks/useTranslate";
import * as yup from "yup";
import {
  alpha,
  Avatar,
  Button,
  Chip,
  Collapse,
  Grid,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { CheckboxButtonGroup, TextFieldElement } from "react-hook-form-mui";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import { AnimalItem } from "../../../animals/components/animal-item";
import { yupResolver } from "@hookform/resolvers/yup";
import { uniq } from "lodash";
import { ModalLoading } from "../../../../hooks/useModal";
import { Add } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { BatchItem } from "../../../animals/components/batch-item";
import { FlockItem } from "../../../animals/components/flock-item";
import { managementQuestions } from "../../../animals/constraints/management-questions";

const NewManagement = () => {
  const snackbar = useSnackbar();
  const ready = useRef(false);
  const { errorNotification, successNotification } = useNotification();
  const navigate = useNavigate();

  const { propertiesIds, user } = useAuth();
  const { translate, currentLanguage } = useTranslate();

  const [animals, setAnimals] = useState<IAnimal[]>([]);
  const [batchs, setBatchs] = useState<IBatch[]>([]);
  const [flocks, setFlocks] = useState<IFlock[]>([]);
  const [option, setOption] = useState<IManagementOptions>("");
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(0);

  const [form, setForm] = useState<Record<string, any>>({});

  const steps = [
    translate("management.title_step_1"),
    translate("management.title_step_2"),
    translate("management.title_step_3"),
  ];

  const managementOptions = useMemo(
    () =>
      managementQuestions.map((question) => ({
        title: question.category[currentLanguage.code ?? "en-us"],
        options: question.children.map((option) => ({
          label: option.label[currentLanguage.code ?? "en-us"],
          id: option.value,
        })),
      })),
    [currentLanguage.code]
  );

  const submitForm = useCallback(
    async (animals: IManagedAnimal[]) => {
      try {
        setLoading(true);
        const response = await createManagement({
          id_user: user.id,
          id_farm: propertiesIds[0],
          author: user.name,
          nameManagement: form.name_management,
          attributes: {
            activity: (form.options ?? []).map((o: any) => o.id),
            animals: animals.map((a) => ({
              id: a.id,
              name: a.name,
              result: a.result,
            })),
          },
        });
        setLoading(false);
        successNotification(translate("management.success_message"));
        navigate(`/managements/${response.data.id}`);
      } catch {
        errorNotification(translate("management.error_message"));
        setLoading(false);
      }
    },
    [
      errorNotification,
      form.name_management,
      form.options,
      navigate,
      propertiesIds,
      successNotification,
      translate,
      user.id,
      user.name,
    ]
  );

  const loadAllFlocks = async (id: string) => {
    const data = await getAllFlocks(id);

    const flocksFormatted = data.map(
      ({ id, name, animal }: IResponseBatchOrFlockAxios) => ({
        id,
        name,
        animals: animal.data,
        count: animal.meta.records,
      })
    );

    setFlocks(flocksFormatted);
  };

  const loadAllBatchs = async (id: string) => {
    const data = await getAllBatchs(id);

    const batchsFormatted = data.map(
      ({ id, name, animal }: IResponseBatchOrFlockAxios) => ({
        id,
        name,
        animals: animal.data,
        count: animal.meta.records,
      })
    );

    setBatchs(batchsFormatted);
  };

  const loadAllAnimals = async (id: string) => {
    const animals = await allAnimals(id);
    setAnimals(animals);
  };

  const [currentAnimalManagement, setCurrentAnimalManagement] =
    useState<IAnimal>();

  const secondFormValidationSchema = yup.object().shape({
    name_management: yup
      .string()
      .required(translate("management.name_management_required")),
  });

  const firstForm = useForm();
  const firstFormValues = firstForm.watch();

  const secondForm = useForm<any>({
    resolver: yupResolver(secondFormValidationSchema),
    defaultValues: {
      animals: [],
      batchs: [],
      flocks: [],
    },
  });
  const secondFormValues = secondForm.watch();

  const secondFormAnimals = useFieldArray({
    name: "animals",
    control: secondForm.control,
  });
  const secondFormBatchs = useFieldArray({
    name: "batchs",
    control: secondForm.control,
  });
  const secondFormFlocks = useFieldArray({
    name: "flocks",
    control: secondForm.control,
  });

  const tertiaryForm = useForm();
  // const tertiaryFormValues = secondForm.watch();

  const onSubmitFirstForm = useCallback(
    (values: any) => {
      console.log(values, managementOptions);

      const managements =
        managementOptions
          .reduce((arr, { options }) => [...arr, ...options] as any, [])
          .filter((m: any) => (values.options ?? []).includes(m.id)) ?? [];
      setForm((old) => ({ ...old, options: managements }));
      setCurrent(1);
    },
    [managementOptions]
  );

  const onSubmitSecondForm = useCallback(
    (values: any) => {
      let animalList =
        animals.filter((a) => !!(values?.animals ?? []).includes(a.id)) ?? [];

      if (Array.isArray(values.batchs)) {
        batchs.forEach((batch) => {
          if ((values.batchs ?? []).includes(batch.id)) {
            const batchAnimals = batch.animals ?? [];
            animalList = uniq([...animalList, ...batchAnimals]);
          }
        });
      }

      if (Array.isArray(values.flocks)) {
        flocks.forEach((flock) => {
          if ((values.flocks ?? []).includes(flock.id)) {
            const flocksAnimals = flock.animals ?? [];
            animalList = [...animalList, ...flocksAnimals];
          }
        });
      }

      setForm((old) => ({
        ...old,
        name_management: values.name_management,
        animals: animalList.map((a) => ({
          id: a.id,
          name: a.name,
          photo: a.photo,
          status: "pending",
          result: null,
        })),
      }));
      setCurrentAnimalManagement(animalList?.[0]);
      setCurrent(2);
    },
    [animals, batchs, flocks]
  );

  const onSubmitTertiaryForm = useCallback(
    (values: any) => {
      setForm((old) => {
        const aa = old.animals.map((a: any) => {
          if (a.id === currentAnimalManagement?.id) {
            return {
              ...a,
              result: values,
              status: "completed",
            };
          }

          return a;
        });

        if (aa.some((a: any) => a.status === "pending")) {
          setCurrentAnimalManagement(
            aa.filter((a: any) => a.status === "pending")?.[0] ?? null
          );
        } else {
          submitForm(form.animals.filter((a: any) => a.status === "completed"));
          setCurrentAnimalManagement(undefined);
        }

        return {
          ...old,
          animals: aa,
        };
      });

      tertiaryForm.reset({});
      tertiaryForm.trigger();
    },
    [currentAnimalManagement?.id, form.animals, submitForm, tertiaryForm]
  );

  const removeAnimal = useCallback(
    (animal: IAnimal) => {
      setForm((old) => {
        const aa = (old.animals ?? []).map((a: any) => ({
          ...a,
          status: animal.id === a.id ? "deleted" : a.status,
        }));

        if (aa.some((a: any) => a.status === "pending")) {
          setCurrentAnimalManagement(
            aa.filter((a: any) => a.status === "pending")?.[0] ?? null
          );
        } else {
          submitForm(form.animals.filter((a: any) => a.status === "completed"));
          setCurrentAnimalManagement(undefined);
        }

        return {
          ...old,
          animals: aa,
        };
      });

      tertiaryForm.reset({});
      tertiaryForm.trigger();
    },
    [form.animals, submitForm, tertiaryForm]
  );

  const boot = useCallback(
    async (id: string) => {
      try {
        loadAllAnimals(id);
        loadAllBatchs(id);
        loadAllFlocks(id);
      } catch {
        errorNotification(translate("global.error_batch_or_flock"));
      }
    },
    [translate, errorNotification]
  );

  useEffect(() => {
    if (propertiesIds.length > 0 && !ready.current) {
      ready.current = true;
      boot(propertiesIds[0]);
    }
  }, [propertiesIds, boot]);

  return (
    <Stack spacing={4}>
      <HeaderTitle
        title={translate("management.title_new")}
        url="/managements"
        showIcon
      />
      <Stepper activeStep={current}>
        {steps.map((step, key) => (
          <Step key={key}>
            <StepLabel>{step}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Paper sx={{ p: 3 }}>
        <Collapse in={current === 0} unmountOnExit>
          <FormProvider {...firstForm}>
            <form onSubmit={firstForm.handleSubmit(onSubmitFirstForm)}>
              <Stack spacing={2}>
                {managementOptions.map(({ title, options }) => (
                  <Stack key={title} spacing={1}>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        color: "primary.main",
                        fontSize: 14,
                      }}
                    >
                      {title}
                    </Typography>
                    <CheckboxButtonGroup row name="options" options={options} />
                  </Stack>
                ))}
                <Stack>
                  <Button
                    color="secondary"
                    variant="contained"
                    type="submit"
                    disabled={(firstFormValues?.options?.length ?? 0) === 0}
                    sx={{ alignSelf: "flex-end" }}
                  >
                    {translate("management.label_btn_step_1")}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </FormProvider>
        </Collapse>
        <Collapse in={current === 1} unmountOnExit>
          <FormProvider {...secondForm}>
            <form onSubmit={secondForm.handleSubmit(onSubmitSecondForm)}>
              <Stack spacing={2}>
                <TextFieldElement
                  name="name_management"
                  label={translate("management.name_management")}
                />
                <Stack spacing={1}>
                  <Typography
                    sx={{
                      color: "primary.main",
                      fontWeight: "bold",
                      fontSize: 14,
                    }}
                  >
                    {translate("management.details_management")}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {managementOptions
                      .reduce<any[]>(
                        (arr, item) => [...arr, ...item.options],
                        []
                      )
                      .filter((m) =>
                        (firstFormValues?.options ?? []).includes(m.id)
                      )
                      .map((item) => (
                        <Chip label={item.label} key={item.id} />
                      ))}
                  </Stack>
                </Stack>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1}>
                    <Typography
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        fontSize: 14,
                      }}
                    >
                      {translate("management.animals_managements")}
                    </Typography>
                    <Chip size="small" label={animals.length} />
                  </Stack>
                  <Stack spacing={1} direction="row">
                    {!option && (
                      <>
                        <Paper
                          component={Button}
                          startIcon={<Add />}
                          sx={{
                            bgcolor: "grey.100",
                            p: 2,
                            flex: 1,
                            textTransform: "capitalize",
                          }}
                          elevation={0}
                          onClick={() => {
                            if (animals.length > 0) {
                              setOption("animals");
                            } else {
                              snackbar.enqueueSnackbar(
                                translate("management.any_animal"),
                                {
                                  variant: "error",
                                }
                              );
                            }
                          }}
                        >
                          {translate("management.select_option_1")}
                        </Paper>
                        <Paper
                          component={Button}
                          startIcon={<Add />}
                          sx={{
                            bgcolor: "grey.100",
                            p: 2,
                            flex: 1,
                            textTransform: "capitalize",
                          }}
                          elevation={0}
                          onClick={() => {
                            if (batchs.length > 0) {
                              setOption("batch");
                            } else {
                              snackbar.enqueueSnackbar(
                                translate("management.any_batch"),
                                {
                                  variant: "error",
                                }
                              );
                            }
                          }}
                        >
                          {translate("management.select_option_2")}
                        </Paper>
                        <Paper
                          component={Button}
                          startIcon={<Add />}
                          sx={{
                            bgcolor: "grey.100",
                            p: 2,
                            flex: 1,
                            textTransform: "capitalize",
                          }}
                          elevation={0}
                          onClick={() => {
                            if (flocks.length > 0) {
                              setOption("flock");
                            } else {
                              snackbar.enqueueSnackbar(
                                translate("management.any_flock"),
                                {
                                  variant: "error",
                                }
                              );
                            }
                          }}
                        >
                          {translate("management.select_option_3")}
                        </Paper>
                        {animals.length === 0 &&
                          batchs.length === 0 &&
                          flocks.length === 0 && (
                            <span>
                              {translate("management.without_items_management")}
                            </span>
                          )}
                      </>
                    )}
                    <Stack spacing={1} sx={{ flex: 1 }}>
                      <Collapse in={option === "animals"} unmountOnExit>
                        <Stack spacing={1}>
                          {animals.map((animal) => (
                            <AnimalItem
                              key={animal.id}
                              animal={animal}
                              checked={(
                                secondFormValues.animals ?? []
                              ).includes(animal.id)}
                              onChange={(a, checked) => {
                                if (checked) {
                                  secondFormAnimals.append(a.id);
                                } else {
                                  secondFormAnimals.remove(
                                    (secondFormValues.animals ?? []).findIndex(
                                      (id: string) => a.id === id
                                    )
                                  );
                                }
                              }}
                            />
                          ))}
                        </Stack>
                      </Collapse>
                      <Collapse in={option === "batch"} unmountOnExit>
                        <Stack spacing={1}>
                          {batchs.map((batch) => (
                            <BatchItem
                              key={batch.id}
                              batch={batch}
                              onChange={(b: IBatch, checked: boolean) => {
                                if (checked) {
                                  secondFormBatchs.append(b.id);
                                } else {
                                  secondFormBatchs.remove(
                                    (secondFormValues.batchs ?? []).findIndex(
                                      (id: string) => b.id === id
                                    )
                                  );
                                }
                              }}
                              checked={(secondFormValues.batchs ?? []).includes(
                                batch.id
                              )}
                            />
                          ))}
                        </Stack>
                      </Collapse>
                      <Collapse in={option === "flock"} unmountOnExit>
                        <Stack spacing={1}>
                          {flocks.map((flock) => (
                            <FlockItem
                              key={flock.id}
                              flock={flock}
                              checked={(secondFormValues.flocks ?? []).includes(
                                flock.id
                              )}
                              onChange={(b: IFlock, checked: boolean) => {
                                if (checked) {
                                  secondFormFlocks.append(b.id);
                                } else {
                                  secondFormFlocks.remove(
                                    (secondFormValues.flocks ?? []).findIndex(
                                      (id: string) => b.id === id
                                    )
                                  );
                                }
                              }}
                            />
                          ))}
                        </Stack>
                      </Collapse>
                    </Stack>
                  </Stack>
                </Stack>
                <Stack>
                  <Button
                    sx={{ alignSelf: "flex-end" }}
                    variant="contained"
                    color="secondary"
                    disabled={
                      (secondFormValues.flocks ?? []).length === 0 &&
                      (secondFormValues.animals ?? []).length === 0 &&
                      (secondFormValues.batchs ?? []).length === 0
                    }
                    type="submit"
                  >
                    {translate("management.label_btn_step_1")}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </FormProvider>
        </Collapse>

        <Collapse in={current === 2} unmountOnExit>
          <FormProvider {...tertiaryForm}>
            <form
              onSubmit={tertiaryForm.handleSubmit(onSubmitTertiaryForm)}
              noValidate
            >
              <Stack spacing={3} direction="row">
                <Collapse
                  in={!!currentAnimalManagement}
                  orientation="horizontal"
                  unmountOnExit
                >
                  <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
                    <Avatar
                      component={Paper}
                      elevation={2}
                      variant="rounded"
                      sx={{ width: 150, height: 150 }}
                      src={currentAnimalManagement?.photo ?? image}
                    />
                    <Stack spacing={2} direction="column" sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 20, fontWeight: "bold" }}>
                        {currentAnimalManagement?.name}
                      </Typography>
                      {(form.options ?? []).map((o: any) => (
                        <TextFieldElement
                          sx={{ flex: 1 }}
                          fullWidth
                          required
                          validation={{
                            required: true,
                          }}
                          key={o.id}
                          label={o.label}
                          name={o.id}
                        />
                      ))}
                      <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="flex-end"
                      >
                        <Button
                          onClick={() => {
                            if (currentAnimalManagement) {
                              removeAnimal(currentAnimalManagement);
                            }
                          }}
                        >
                          {translate("management.remove_animal")}
                        </Button>
                        <Button
                          type="submit"
                          color="secondary"
                          variant="contained"
                        >
                          {translate("management.label_btn_save")}
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                </Collapse>
                <Stack spacing={1} sx={{ flex: 1 }}>
                  {(form.animals ?? []).map((animal: any) => (
                    <Paper
                      key={animal.id}
                      sx={(theme) => ({
                        bgcolor:
                          animal.status === "completed"
                            ? alpha(theme.palette.success.main, 0.2)
                            : animal.id === currentAnimalManagement?.id
                            ? alpha(theme.palette.warning.main, 0.2)
                            : animal.status === "deleted"
                            ? alpha(theme.palette.error.main, 0.2)
                            : "grey.100",
                        p: 1,
                      })}
                      elevation={0}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item md={false}>
                          <Avatar src={animal.photo ?? image} />
                        </Grid>
                        <Grid item md>
                          <Typography sx={{ fontSize: 14, fontWeight: "bold" }}>
                            {animal.name}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            </form>
          </FormProvider>
        </Collapse>

        <Collapse in={loading} unmountOnExit>
          <ModalLoading />
        </Collapse>
      </Paper>
    </Stack>
  );
};

export { NewManagement };
